<template>
    <frm-modal-component
        ref="modal"
        :header-description="$t('settings.external-interfaces.online-planner.employees.modal.description')"
        :loading="saving"
        :save-disabled="loading || !hasChanges"
        cancel-variant="danger"
        class="online-planner-schedules"
        data-testid="online-planner-schedules-modal"
        @save="save"
    >
        <template #header>
            {{ $t('settings.external-interfaces.online-planner.employees.modal.title', {name: employee.name}) }}
        </template>

        <div v-if="loading" class="online-planner-employees__loading">
            <frm-spinner />
        </div>

        <div v-else>
            <section v-for="location in locations" :key="location.id" class="online-planner-schedules__schedule">
                <header class="online-planner-schedules__schedule-header">
                    <span>
                        <strong>{{ location.name }}</strong>
                        <small>{{ $t('settings.external-interfaces.online-planner.employees.modal.online-count', {
                            online: onlineCount(location),
                            total: location.dayparts.length,
                        }) }}</small>
                    </span>
                    <button class="online-planner-schedules__bulk-action" type="button" @click="toggleAll(location)">
                        {{ bulkActionLabel(location) }}
                    </button>
                </header>

                <div class="online-planner-schedules__dayparts">
                    <div
                        v-for="dayPart in location.dayparts"
                        :key="dayPart.id"
                        class="online-planner-schedules__daypart"
                        :class="{'online-planner-schedules__daypart--offline': !dayPart.is_available_for_online_planner}"
                    >
                        <span class="online-planner-schedules__slot">
                            <span class="online-planner-schedules__day">{{ weekday(dayPart.day_of_the_week) }}</span>
                            <span class="online-planner-schedules__time">{{ formatTime(dayPart.start) }}–{{ formatTime(dayPart.end) }}</span>
                        </span>
                        <span class="online-planner-schedules__control">
                            <span
                                class="online-planner-schedules__state"
                                :class="{'online-planner-schedules__state--online': dayPart.is_available_for_online_planner}"
                            >
                                {{ dayPart.is_available_for_online_planner
                                    ? $t('settings.external-interfaces.online-planner.employees.modal.online')
                                    : $t('settings.external-interfaces.online-planner.employees.modal.offline') }}
                            </span>
                            <span class="form-switch form-switch--large">
                                <input
                                    :id="'online-planner-daypart-' + dayPart.id"
                                    v-model="dayPart.is_available_for_online_planner"
                                    :aria-label="toggleLabel(dayPart)"
                                    class="form-switch__input"
                                    role="switch"
                                    type="checkbox"
                                >
                                <label class="form-switch__label" :for="'online-planner-daypart-' + dayPart.id">
                                    <span class="form-switch__knob">
                                        <span class="form-switch__dot" />
                                    </span>
                                    <span class="form-switch__label-text">
                                        {{ $t('settings.external-interfaces.online-planner.employees.modal.online') }}
                                    </span>
                                </label>
                            </span>
                        </span>
                    </div>
                </div>
            </section>

            <div v-if="!locations.length" class="online-planner-schedules__empty">
                {{ $t('settings.external-interfaces.online-planner.employees.modal.no-dayparts') }}
            </div>
        </div>

        <template #buttons>
            <span v-if="!loading" class="online-planner-schedules__total">
                {{ $tc('settings.external-interfaces.online-planner.employees.modal.total-online', totalOnline, {count: totalOnline}) }}
            </span>
        </template>
    </frm-modal-component>
</template>

<script>
import axios from 'axios';
import FormatsDayParts from '../../mixins/FormatsDayParts';
import HandlesErrorResponses from '../../mixins/HandlesErrorResponses';

export default {
    mixins: [FormatsDayParts, HandlesErrorResponses],

    props: {
        employee: {
            type: Object,
            required: true,
        },

        url: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            schedules: [],
            originalAvailability: {},
            loading: true,
            saving: false,
        };
    },

    computed: {
        changedDayParts() {
            return this.schedules
                .flatMap(schedule => schedule.dayparts)
                .filter(dayPart => this.originalAvailability[dayPart.id] !== dayPart.is_available_for_online_planner);
        },

        hasChanges() {
            return this.changedDayParts.length > 0;
        },

        totalOnline() {
            return this.schedules.reduce((total, schedule) => total + this.onlineCount(schedule), 0);
        },

        locations() {
            const locations = [];

            this.schedules.forEach(schedule => {
                schedule.dayparts.forEach(dayPart => {
                    const id = dayPart.location_id || 'none';
                    const name = dayPart.location || this.$t('settings.external-interfaces.online-planner.employees.modal.no-location');
                    let location = locations.find(item => item.id === id);

                    if (!location) {
                        location = { id, name, dayparts: [] };
                        locations.push(location);
                    }

                    location.dayparts.push(dayPart);
                });
            });

            return locations;
        },
    },

    mounted() {
        this.fetchSchedules();
    },

    methods: {
        async fetchSchedules() {
            this.loading = true;

            try {
                const data = (await axios.get(this.url)).data.data;
                this.schedules = data.schedules;
                this.originalAvailability = Object.fromEntries(this.schedules
                    .flatMap(schedule => schedule.dayparts)
                    .map(dayPart => [dayPart.id, dayPart.is_available_for_online_planner]));
            } catch (error) {
                await this.handleErrorResponse(error);
                this.$refs.modal.closeModal();
            } finally {
                this.loading = false;
            }
        },

        onlineCount(group) {
            return group.dayparts.filter(dayPart => dayPart.is_available_for_online_planner).length;
        },

        toggleLabel(dayPart) {
            return `${this.weekday(dayPart.day_of_the_week)} ${this.formatTime(dayPart.start)}-${this.formatTime(dayPart.end)} ${this.$t('settings.external-interfaces.online-planner.employees.modal.online').toLowerCase()}`;
        },

        bulkActionLabel(group) {
            const action = this.onlineCount(group) === group.dayparts.length ? 'disable-all' : 'enable-all';

            return this.$t(`settings.external-interfaces.online-planner.employees.modal.${action}`);
        },

        toggleAll(group) {
            const available = this.onlineCount(group) !== group.dayparts.length;
            group.dayparts.forEach(dayPart => {
                dayPart.is_available_for_online_planner = available;
            });
        },

        async save() {
            this.saving = true;

            try {
                const response = await axios.patch(this.url, {
                    schedule_dayparts: this.changedDayParts.map(dayPart => ({
                        id: dayPart.id,
                        is_available_for_online_planner: dayPart.is_available_for_online_planner,
                    })),
                });

                this.$refs.modal.closeModal(response.data.data);
            } catch (error) {
                await this.handleErrorResponse(error);
                await this.fetchSchedules();
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
